/**
 * Pure helpers for turning raw scanner output into a code and an add-on.
 * Kept free of the WASM module so they can be unit-tested without a camera.
 */

export interface DecodedScan {
  /** Main symbol, digits only. */
  code: string;
  /** EAN-2 or EAN-5 supplemental, if one was read. */
  addon: string | null;
  /** Symbology as reported by the reader, e.g. "UPC-A". */
  format: string;
}

const ADDON_PATTERN = /^(\d{2}|\d{5})$/;

/** Digits of the main symbol per reported format, to split off an add-on. */
const FORMAT_LENGTH: Record<string, number> = {
  EAN13: 13,
  'EAN-13': 13,
  UPCA: 12,
  'UPC-A': 12,
  EAN8: 8,
  'EAN-8': 8,
  UPCE: 8,
  'UPC-E': 8,
};

/**
 * Splits raw decoder output into code and add-on.
 *
 * What zxing-wasm 3.1.6 actually returns (checked against generated
 * barcodes in decoder.test.ts): the supplemental is appended to `text`
 * without a separator ("076194130012200111") and also reported in `extra` as
 * JSON (`EanAddOn`), a field the library marks experimental; UPC-A comes back
 * as an EAN-13 with a leading zero. Each of these is handled defensively, so
 * a change in a later version degrades gracefully instead of silently
 * producing an 18 digit "code".
 */
export function decodeScan(raw: {
  text: string;
  extra?: string;
  format: string;
}): DecodedScan | null {
  let digits = raw.text.replace(/\D/g, '');
  let addon: string | null = null;

  if (raw.extra) {
    try {
      const extra = JSON.parse(raw.extra) as { EanAddOn?: unknown };
      if (typeof extra.EanAddOn === 'string' && ADDON_PATTERN.test(extra.EanAddOn)) {
        addon = extra.EanAddOn;
      }
    } catch {
      // Malformed extra data: fall back to the length of the format.
    }
  }

  if (addon && digits.endsWith(addon) && digits.length - addon.length >= 8) {
    digits = digits.slice(0, -addon.length);
  } else if (!addon) {
    const expected = FORMAT_LENGTH[raw.format];
    if (expected && (digits.length === expected + 2 || digits.length === expected + 5)) {
      addon = digits.slice(expected);
      digits = digits.slice(0, expected);
    }
  }

  if (digits.length < 8) return null;

  // An EAN-13 with a leading zero is a UPC-A; report it in its usual form.
  if (digits.length === 13 && digits.startsWith('0')) digits = digits.slice(1);

  return { code: digits, addon, format: raw.format };
}

/** Same physical code, regardless of whether the add-on was caught yet. */
export function sameMainCode(a: DecodedScan, b: DecodedScan): boolean {
  return a.code === b.code;
}

/**
 * Decides whether a scan is a repeat of the last accepted one. The camera
 * sees the same barcode in many consecutive frames, and a handscanner can
 * double-fire; both must count as one scan.
 */
export function isDuplicateScan(
  scan: DecodedScan,
  last: { scan: DecodedScan; at: number } | null,
  now: number,
  windowMs = 3000,
): boolean {
  if (!last) return false;
  return last.scan.code === scan.code && last.scan.addon === scan.addon && now - last.at < windowMs;
}

/**
 * Keyboard-wedge detection. Handscanners type the digits much faster than a
 * person can and finish with Enter; a human typing digits into the page should
 * not trigger a scan.
 */
export interface KeyStroke {
  key: string;
  at: number;
}

export function detectWedgeScan(
  strokes: KeyStroke[],
  options: { minLength?: number; maxAverageGapMs?: number } = {},
): string | null {
  const minLength = options.minLength ?? 8;
  const maxAverageGapMs = options.maxAverageGapMs ?? 35;

  const digits = strokes.filter((stroke) => /^\d$/.test(stroke.key));
  if (digits.length < minLength || digits.length !== strokes.length) return null;

  const first = digits[0];
  const last = digits[digits.length - 1];
  if (!first || !last) return null;

  const averageGap = (last.at - first.at) / Math.max(1, digits.length - 1);
  if (averageGap > maxAverageGapMs) return null;

  return digits.map((stroke) => stroke.key).join('');
}
